import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "motion/react";
import { SpinnerIcon } from "../icons/spinner";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/16/solid";
import { useMemo, useState, useEffect } from "react";
import { imagineStore } from "@/stores/imagine.store";
import ProgressHeaderTexture from "@/images/in-chat-progress-header-texture.png";
import { type ChatSlice } from "@/stores/slices/chat.slice";

export type PromptInputTodoListProps = {
  className?: string;
};

const TODO_ITEMS_VISIBLE_THRESHOLD = 99;

const statusOrder = [
  "failed",
  "completed",
  "skipped",
  "in-progress",
  "pending",
];

export function PromptInputTodoList({ className }: PromptInputTodoListProps) {
  const [showMore, _setShowMore] = useState(false);
  const todos = imagineStore.use.chat.todos();

  const textToRenderInHeader = useMemo(() => {
    const numCompletedTasks = todos.filter(
      (todo) => todo.status === "completed",
    ).length;
    return `${numCompletedTasks} out of ${todos.length} tasks completed`;
  }, [todos]);

  const scrollToBottom$ = imagineStore.use.chat.scrollToBottom$();

  const scrollToBottom = () => {
    scrollToBottom$.next();
  };

  const isOverThreshold = todos.length > TODO_ITEMS_VISIBLE_THRESHOLD;

  const tasksToRender =
    isOverThreshold && !showMore
      ? todos.slice(-TODO_ITEMS_VISIBLE_THRESHOLD)
      : todos;

  const orderedTasksToRender = [...tasksToRender].sort((a, b) => {
    return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
  });

  return (
    <AnimatePresence mode="wait">
      {todos.length > 0 && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          onAnimationComplete={() => {
            scrollToBottom();
          }}
          transition={{
            duration: 0.3,
            ease: [0.32, 0.72, 0, 1],
          }}
          className={cn(
            "bg-primary rounded-t-[20px] mt-[-3px] mx-[-3px]",
            className,
          )}
        >
          <div className="h-8.5 flex flex-col bg-primary rounded-t-[20px] overflow-hidden">
            <div className="flex-1 flex items-center text-sidebar px-4 relative">
              <div className="relative z-10 w-full flex justify-between items-center text-[0.75rem]">
                <div className="font-mono tracking-tight">
                  {textToRenderInHeader}
                </div>
              </div>
              <div className="absolute right-0 top-0 w-full h-full pointer-events-none">
                <img
                  src={ProgressHeaderTexture}
                  alt="Progress Header Texture"
                  className="absolute right-0 top-0 h-full object-cover dark:invert"
                  style={{
                    maskImage:
                      "linear-gradient(to right, transparent 0%, black 40%, black 100%)",
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className={cn(
              `bg-sidebar text-foreground/80 gap-[8px] flex flex-col p-3 rounded-t-[20px] overflow-hidden`,
            )}
          >
            <AnimatePresence mode="popLayout">
              {orderedTasksToRender.map((todo) => (
                <motion.div
                  key={todo.id}
                  layout
                  layoutId={todo.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8, height: 0 }}
                  transition={{
                    layout: { duration: 0.3, ease: [0.32, 0.72, 0, 1] },
                    opacity: { duration: 0.2 },
                    scale: { duration: 0.2 },
                  }}
                  className={cn("flex gap-2.5 items-center", `h-[18px]`)}
                >
                  <div className="">
                    <TodoStatus status={todo.status} />
                  </div>
                  <div
                    className={cn(
                      "transition-all line-clamp-1 font-light text-[0.8rem]",
                      (todo.status === "completed" ||
                        todo.status === "skipped" ||
                        todo.status === "failed") &&
                        "line-through opacity-40",
                    )}
                  >
                    {todo.title}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function TodoStatus({
  status,
}: {
  status: ChatSlice["todos"][number]["status"];
}) {
  return (
    <AnimatePresence>
      {status === "pending" && (
        <div className="size-[13px] bg-background rounded-full border"></div>
      )}
      {status === "in-progress" && (
        <SpinnerIcon className="size-[13px] animate-spin" />
      )}
      {(status === "completed" || status === "skipped") && (
        <CheckCircleIcon className="size-[13px]" />
      )}
      {status === "failed" && (
        <XCircleIcon className="size-[13px] text-destructive" />
      )}
    </AnimatePresence>
  );
}
